// FILE: src/components/help/Glossary.jsx
// The course glossary (MTO 18.1: accessible throughout the course; 9.3:
// acronyms spelled out). Searchable list, grouped by first letter. Used by
// the glossary pages and by the drawer in the top bar (every page, including
// the lesson player).
import React, { useMemo, useState } from "react";
import { Box, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getGlossary } from "../../services/courseInfo";
import useAsync from "./useAsync";
import { EmptyState, ErrorState, LoadingState } from "../ui";

export function useGlossary() {
  return useAsync(getGlossary, []);
}

function normalise(s) {
  return String(s || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default function Glossary({ headingLevel = 2, dense = false, autoFocus = false }) {
  const { data, error, loading, reload } = useGlossary();
  const [query, setQuery] = useState("");
  const terms = useMemo(() => {
    const list = (data?.terms || data || []).slice().sort((a, b) => a.term.localeCompare(b.term, "en-CA"));
    const q = normalise(query.trim());
    return q ? list.filter((t) => normalise(`${t.term} ${t.acronym || ""} ${t.definition}`).includes(q)) : list;
  }, [data, query]);
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of terms) {
      const letter = t.term[0].toUpperCase();
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter).push(t);
    }
    return [...map.entries()];
  }, [terms]);
  const H = `h${headingLevel}`;

  return (
    <Box>
      <TextField
        label="Search the glossary"
        type="search"
        fullWidth
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon aria-hidden /></InputAdornment> } }}
        sx={{ mb: 3 }}
      />
      {loading && !data ? (
        <LoadingState variant="inline" label="Loading the glossary…" />
      ) : error ? (
        <ErrorState error={error} title="We couldn't load the glossary" onRetry={reload} />
      ) : terms.length === 0 ? (
        <EmptyState compact title="No matching terms" description="Try a shorter word, or ask student support what something means." />
      ) : (
        <Box aria-live="polite">
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            {terms.length} {terms.length === 1 ? "term" : "terms"}
          </Typography>
          <Stack spacing={dense ? 2 : 3}>
            {groups.map(([letter, items]) => (
              <Box key={letter} component="section" aria-label={`Terms starting with ${letter}`}>
                <Typography component={H} sx={{ fontWeight: 700, fontSize: "1.25rem", color: "primary.main", mb: 1 }}>
                  {letter}
                </Typography>
                <Stack component="dl" spacing={1.5} sx={{ m: 0 }}>
                  {items.map((t) => (
                    <Box key={t.term} sx={{ pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                      <Typography component="dt" sx={{ fontWeight: 650 }}>
                        {t.term}
                        {t.acronym ? ` (${t.acronym})` : ""}
                      </Typography>
                      <Typography component="dd" sx={{ m: 0, mt: 0.25 }}>
                        {t.definition}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
